// ============================================================================
// REMINDERS PAGE v2 — Tabs: Enviados (default) | Planificados | Cancelados
// ============================================================================
import { useState, useEffect, useCallback } from 'react';
import {
  Bell, CheckCircle, XCircle, Clock, AlertTriangle, X,
  RefreshCw, Phone, Edit2, Calendar, Filter,
} from 'lucide-react';
import { remindersApi, type ReminderDto } from '../api/reminders';
import { useToast } from '../components/ui/Toast';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { ReminderModal } from '../components/ui/ReminderModal';

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('T')[0].split('-');
  return `${d}/${m}/${y}`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ── Status badge ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  PENDIENTE: { label: 'Pendiente', bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
  ENVIADO:   { label: 'Enviado',   bg: 'bg-blue-100',  text: 'text-blue-700',  icon: CheckCircle },
  FALLIDO:   { label: 'Fallido',   bg: 'bg-red-100',   text: 'text-red-700',   icon: XCircle },
  CANCELADO: { label: 'Cancelado', bg: 'bg-gray-100',  text: 'text-gray-500',  icon: X },
} as const;

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.PENDIENTE;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <Icon className="h-3 w-3" />{cfg.label}
    </span>
  );
}

// ── Outcome badge ─────────────────────────────────────────────────────────────
function OutcomeBadge({ outcome }: { outcome: string | null }) {
  if (!outcome) return <span className="text-xs text-gray-400">Sin gestionar</span>;
  if (outcome === 'CITED')
    return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"><CheckCircle className="h-3 w-3" />Con cita</span>;
  return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700"><XCircle className="h-3 w-3" />Sin cita</span>;
}

// ── Tipos de tab ──────────────────────────────────────────────────────────────
type Tab = 'sent' | 'planned' | 'cancelled';

const TABS: { id: Tab; label: string; statuses: string[] }[] = [
  { id: 'sent',      label: 'Enviados',     statuses: ['ENVIADO', 'FALLIDO'] },
  { id: 'planned',   label: 'Planificados', statuses: ['PENDIENTE'] },
  { id: 'cancelled', label: 'Cancelados',   statuses: ['CANCELADO'] },
];

// ── Componente principal ──────────────────────────────────────────────────────
export function RemindersPage() {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<Tab>('sent');
  const [items, setItems] = useState<ReminderDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState<string>('all');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Modales
  const [cancelDialog, setCancelDialog] = useState<{ isOpen: boolean; item: ReminderDto | null }>({ isOpen: false, item: null });
  const [outcomeDialog, setOutcomeDialog] = useState<{ isOpen: boolean; item: ReminderDto | null; outcome: 'CITED' | 'NOT_CITED' }>({ isOpen: false, item: null, outcome: 'CITED' });
  const [editModal, setEditModal] = useState<{ isOpen: boolean; item: ReminderDto | null }>({ isOpen: false, item: null });
  const [isActing, setIsActing] = useState(false);

  const currentTab = TABS.find(t => t.id === activeTab)!;

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      // Cargar todos los status del tab actual
      const results = await Promise.all(
        currentTab.statuses.map(status =>
          remindersApi.getAll({
            status,
            dateFrom: dateFrom || undefined,
            dateTo: dateTo || undefined,
          })
        )
      );
      const merged = results.flat().sort((a, b) =>
        new Date(b.scheduledDate).getTime() - new Date(a.scheduledDate).getTime()
      );
      setItems(merged);
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudieron cargar los recordatorios');
    } finally { setIsLoading(false); }
  }, [activeTab, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const handleCancel = async () => {
    if (!cancelDialog.item) return;
    setIsActing(true);
    try {
      await remindersApi.cancel(cancelDialog.item.id);
      toast.success('Cancelado', 'El recordatorio ha sido cancelado');
      setCancelDialog({ isOpen: false, item: null });
      load();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo cancelar');
    } finally { setIsActing(false); }
  };

  const handleOutcome = async () => {
    if (!outcomeDialog.item) return;
    setIsActing(true);
    try {
      await remindersApi.setOutcome(outcomeDialog.item.id, outcomeDialog.outcome);
      toast.success('Guardado', outcomeDialog.outcome === 'CITED' ? 'Marcado como con cita' : 'Marcado como sin cita');
      setOutcomeDialog({ isOpen: false, item: null, outcome: 'CITED' });
      load();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo actualizar');
    } finally { setIsActing(false); }
  };

  const hasFilters = dateFrom || dateTo;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Recordatorios</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isLoading ? 'Cargando...' : `${items.length} recordatorio${items.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowFilters(f => !f)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition-colors ${
              hasFilters ? 'bg-blue-50 text-[#008DD0] border-[#008DD0]' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
            }`}>
            <Filter className="h-4 w-4" /> Filtros{hasFilters ? ' (activos)' : ''}
          </button>
          <button onClick={load}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors">
            <RefreshCw className="h-4 w-4" /> Actualizar
          </button>
        </div>
      </div>

      {/* Filtros */}
      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4 flex-wrap">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Desde</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#008DD0]" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Hasta</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#008DD0]" />
          </div>
          {hasFilters && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="mt-4 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
              <X className="h-3 w-3" /> Limpiar
            </button>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => { setActiveTab(tab.id); }}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-[#008DD0] text-[#008DD0]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filtro outcome — solo en tab enviados */}
      {activeTab === 'sent' && (
        <div className="flex items-center gap-2">
          {[
            { value: 'all',       label: 'Todos' },
            { value: 'pending',   label: 'Sin gestionar' },
            { value: 'CITED',     label: 'Con cita' },
            { value: 'NOT_CITED', label: 'Sin cita' },
          ].map(opt => (
            <button key={opt.value} onClick={() => setOutcomeFilter(opt.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                outcomeFilter === opt.value
                  ? 'bg-[#008DD0] text-white border-[#008DD0]'
                  : 'text-gray-600 bg-white border-gray-200 hover:bg-gray-50'
              }`}>
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Tabla */}
      {isLoading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Bell className="h-7 w-7 text-gray-400" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-1">Sin recordatorios</h3>
          <p className="text-sm text-gray-500">No hay recordatorios en esta categoría.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Paciente</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Fecha</th>
                {activeTab === 'sent' && (
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
                )}
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Mensaje</th>
                {activeTab === 'sent' && (
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Cita</th>
                )}
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.filter(item => {
                if (activeTab !== 'sent') return true;
                if (outcomeFilter === 'all') return true;
                if (outcomeFilter === 'pending') return !item.appointmentOutcome;
                return item.appointmentOutcome === outcomeFilter;
              }).map(item => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  {/* Paciente */}
                  <td className="px-6 py-4">
                    <p className="font-medium text-gray-900">{item.patientName}</p>
                    {item.patientPhone && (
                      <a href={`tel:${item.patientPhone}`}
                        className="flex items-center gap-1 text-xs text-[#008DD0] hover:underline mt-0.5">
                        <Phone className="h-3 w-3" />{item.patientPhone}
                      </a>
                    )}
                    {item.budgetNumber && <p className="text-xs text-gray-400 mt-0.5">{item.budgetNumber}</p>}
                    {item.treatmentName && <p className="text-xs text-gray-400">{item.treatmentName}</p>}
                  </td>
                  {/* Fecha */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5 text-gray-700">
                      <Calendar className="h-3.5 w-3.5 text-gray-400" />
                      <span className="font-medium">{formatDate(item.scheduledDate)}</span>
                    </div>
                    {item.sentAt && <p className="text-xs text-gray-400 mt-0.5">Enviado: {formatDateTime(item.sentAt)}</p>}
                  </td>
                  {/* Estado (solo tab enviados) */}
                  {activeTab === 'sent' && (
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <StatusBadge status={item.status} />
                        {item.status === 'FALLIDO' && item.failReasonLabel && (
                          <div className="flex items-center gap-1 text-xs text-red-600">
                            <AlertTriangle className="h-3 w-3" />{item.failReasonLabel}
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                  {/* Mensaje */}
                  <td className="px-6 py-4 max-w-xs">
                    <p className="text-xs text-gray-600 truncate" title={item.resolvedMessage}>
                      {item.templateName && <span className="text-gray-400 mr-1">[{item.templateName}]</span>}
                      {item.resolvedMessage}
                    </p>
                  </td>
                  {/* Gestión cita (solo tab enviados) */}
                  {activeTab === 'sent' && (
                    <td className="px-6 py-4">
                      <div className="space-y-1.5">
                        <OutcomeBadge outcome={item.appointmentOutcome} />
                        {!item.appointmentOutcome && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setOutcomeDialog({ isOpen: true, item, outcome: 'CITED' })}
                              className="text-xs px-2 py-1 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-colors">
                              Con cita
                            </button>
                            <button
                              onClick={() => setOutcomeDialog({ isOpen: true, item, outcome: 'NOT_CITED' })}
                              className="text-xs px-2 py-1 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
                              Sin cita
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                  {/* Acciones */}
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {activeTab === 'planned' && (
                        <>
                          <button
                            onClick={() => setEditModal({ isOpen: true, item })}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                            <Edit2 className="h-3.5 w-3.5" /> Editar
                          </button>
                          <button
                            onClick={() => setCancelDialog({ isOpen: true, item })}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors">
                            <X className="h-3.5 w-3.5" /> Cancelar
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Diálogos */}
      <ConfirmDialog
        isOpen={cancelDialog.isOpen}
        onClose={() => !isActing && setCancelDialog({ isOpen: false, item: null })}
        onConfirm={handleCancel}
        isLoading={isActing}
        variant="warning"
        title="¿Cancelar recordatorio?"
        message={`El recordatorio para "${cancelDialog.item?.patientName}" del ${formatDate(cancelDialog.item?.scheduledDate || null)} será cancelado.`}
        confirmText="Cancelar recordatorio"
      />

      <ConfirmDialog
        isOpen={outcomeDialog.isOpen}
        onClose={() => !isActing && setOutcomeDialog({ isOpen: false, item: null, outcome: 'CITED' })}
        onConfirm={handleOutcome}
        isLoading={isActing}
        variant="info"
        title={outcomeDialog.outcome === 'CITED' ? '¿El paciente cogió cita?' : '¿El paciente no cogió cita?'}
        message={`Se marcará el recordatorio de "${outcomeDialog.item?.patientName}" como ${outcomeDialog.outcome === 'CITED' ? 'con cita' : 'sin cita'}.`}
        confirmText="Confirmar"
      />

      {/* Modal editar */}
      {editModal.item && (
        <ReminderModal
          isOpen={editModal.isOpen}
          onClose={() => setEditModal({ isOpen: false, item: null })}
          onSaved={load}
          patientId={editModal.item.patientId}
          patientName={editModal.item.patientName}
          budgetId={editModal.item.budgetId || undefined}
          budgetNumber={editModal.item.budgetNumber || undefined}
          reminderToEdit={editModal.item}
        />
      )}
    </div>
  );
}

export default RemindersPage;
